// screen: Register → verify (the e-mail link). Opens the account (tenant on the Free plan + owner), which signs the
// person in; then a full page load into Studio's first-run checklist — or, for a sign-up that started from a plugin's
// Connect, straight to the /connect consent with the same request.
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ApiError } from '@viewstream/api-client';
import { EmptyState, Skeleton } from '@viewstream/ui';
import { ProductName, LocaleToggle } from '~/app/brand';
import { useLocale } from '~/app/locale';
import { verifySignup } from '~/app/selfserve';

export const Route = createFileRoute('/register/verify')({
  validateSearch: (s: Record<string, unknown>) => ({ token: typeof s['token'] === 'string' ? s['token'] : '' }),
  component: Verify,
});

function Verify() {
  const { t } = useLingui();
  useLocale();
  const { token } = Route.useSearch();
  const [state, setState] = React.useState<{ kind: 'busy' | 'used' | 'expired' | 'invalid' | 'error'; detail?: string }>({ kind: 'busy' });
  const once = React.useRef(false);
  React.useEffect(() => {
    if (once.current) return; // StrictMode: a link opens the account once
    once.current = true;
    if (!token) { setState({ kind: 'invalid' }); return; }
    verifySignup(token).then((r) => {
      const next = r.continue && r.continue.startsWith('/connect?') ? r.continue : `/t/${encodeURIComponent(r.tenant.slug)}?welcome=1`;
      window.location.assign(next); // full load: the new session cookie and /v1/me
    }).catch((ex: unknown) => {
      if (ex instanceof ApiError && ex.status === 409) setState({ kind: 'used', detail: ex.problem?.detail });
      else if (ex instanceof ApiError && ex.status === 410) setState({ kind: 'expired' });
      else if (ex instanceof ApiError && ex.status === 404) setState({ kind: 'invalid' });
      else setState({ kind: 'error', detail: ex instanceof Error ? ex.message : String(ex) });
    });
  }, [token]);
  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-8 shadow-card">
        <div className="mb-6 flex items-center justify-between"><ProductName large /><LocaleToggle /></div>
        {state.kind === 'busy' ? (<><p className="mb-3 text-sm">{t`Opening your account…`}</p><Skeleton className="h-8 w-full" /></>) :
          state.kind === 'used' ? <EmptyState title={t`This link was already used`} description={t`Your account is open — sign in with your e-mail and password.`} /> :
          state.kind === 'expired' ? <EmptyState title={t`The link expired`} description={t`Links are valid for 48 hours. Sign up again to get a new one.`} /> :
          state.kind === 'invalid' ? <EmptyState title={t`Invalid link`} description={t`Open the link from the e-mail exactly as it arrived, or sign up again.`} /> :
          <EmptyState title={t`The account could not be opened`} description={t`Try the link again in a minute. If it keeps failing, write to support@viewstream.co.il.`} />}
        <p className="mt-4 text-center text-sm">
          <a className="text-primary underline-offset-4 hover:underline" href="/login">{t`Sign in`}</a>
          {' · '}
          <a className="text-primary underline-offset-4 hover:underline" href="/register">{t`Sign up`}</a>
        </p>
      </div>
    </main>
  );
}
